import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { api, ApiError, type TournamentDuplicatePreview } from "../lib/api";
import { REGISTRATION_LINK_PREFIX, registrationLinkSlug } from "../lib/registrationLink";

interface DuplicateTournamentDialogProps {
  token: string;
  /** ``slug``: el link de inscripción del torneo anterior (si se renombró
   * ya no sale de su nombre). */
  tournament: { id: number; name: string; slug?: string | null };
  /** De quién queda la copia y si se lleva la forma de cobro (servidor:
   * duplicate_previews). Sin el dato se usa un texto general. */
  preview?: TournamentDuplicatePreview | null;
  onClose: () => void;
}

const NAME_MAX = 200;

/** Lo que el diálogo promete; debe coincidir con duplicate_tournament del backend. */
const COPIED = [
  "Datos del torneo: sede, descripción, organizador, pelota y patrocinadores.",
  "Categorías con su formato, desempates y calendario (tipo de cancha, canchas, horarios).",
  "Formulario de inscripción con sus preguntas y costos.",
  // Las fechas del torneo se ponen solas en la convocatoria; las que el
  // organizador escribió dentro de los textos o del PDF del reglamento no.
  "Reglamento y textos de la convocatoria. Si escribiste fechas dentro de ellos o en el PDF del reglamento, revísalas.",
];

const NOT_COPIED = [
  "El póster: sube el del nuevo torneo.",
  "Los grupos de WhatsApp: agrega el del nuevo torneo.",
  "El PDF propio de la convocatoria y la liga a un ranking: vuelve a ponerlos en el nuevo torneo si los usas.",
  "Jugadores, pagos, grupos, cuadros, partidos y resultados.",
  "El nuevo torneo no sale en torneos123.com hasta que marques «Torneo público» en el paso 1.",
];

/** Renglón de la forma de cobro y en qué lista va. El servidor solo la copia
 * si el dueño de la copia creó el torneo anterior (a él le paga Mercado
 * Pago), y la copia es SIEMPRE del organizador aunque duplique otro admin
 * completo o Torneos123 (revisión ronda 4). */
function paymentLine(preview: TournamentDuplicatePreview | null | undefined): { copied: boolean; text: string } {
  if (!preview) {
    return {
      copied: true,
      text: "La forma de cobro del organizador del torneo. Si el torneo anterior lo creó Torneos123 para tu club, la forma de cobro la eliges en el paso 1 y ahí te avisamos.",
    };
  }
  const owner = preview.owner_name || "quien organiza el torneo";
  if (preview.payment_copied) {
    return preview.owner_is_you
      ? { copied: true, text: "La forma de cobro (Mercado Pago o transferencia)." }
      : { copied: true, text: `La forma de cobro de ${owner}: los pagos de inscripción van a su cuenta.` };
  }
  return preview.owner_is_you
    ? {
        copied: false,
        text: "La forma de cobro, porque el torneo anterior lo creó otra persona (por ejemplo, Torneos123 para tu club): la eliges en el paso 1.",
      }
    : {
        copied: false,
        text: `La forma de cobro, porque el torneo anterior lo creó otra persona: la elige ${owner} en el paso 1.`,
      };
}

/** Aviso cuando la copia NO queda a nombre de quien duplica. */
function ownerNotice(preview: TournamentDuplicatePreview | null | undefined, sourceName: string): string | null {
  if (!preview || preview.owner_is_you) return null;
  const owner = preview.owner_name ? `${preview.owner_name}, quien organiza` : "quien organiza";
  return `El nuevo torneo queda a nombre de ${owner} «${sourceName}», y los pagos de inscripción van a su cuenta. Tú lo sigues administrando.`;
}

/** Mismo nombre: sin acentos, mayúsculas ni espacios de más. */
function comparableName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** En <body> mientras la hoja está abierta: esconde la burbuja de ayuda
 * (Roger), que en el teléfono tapaba «Crear copia». */
export const DUPLICATE_SHEET_OPEN_CLASS = "duplicate-tournament-open";

/** Lo que se puede enfocar dentro de la hoja (para no salir con Tab). */
const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/** Mismas reglas que el Paso 1 (Datos) y el servidor. */
export function duplicateFormError(values: {
  name: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  /** Nombre del torneo anterior: el nuevo debe ser otro (con él se arma su link). */
  previousName?: string;
  /** Link del torneo anterior (``slug``): el nuevo no puede armar el mismo. */
  previousSlug?: string | null;
}): string | null {
  if (!values.name.trim()) return "Escribe el nombre del nuevo torneo.";
  if (values.name.trim().length > NAME_MAX) return `El nombre puede tener máximo ${NAME_MAX} letras.`;
  // Revisión ronda 5: se compara el LINK que arma el servidor, no solo el
  // nombre. «Copa Otoño 2025.» pasaba y el servidor le agregaba al link unas
  // letras al azar que se quedaban para siempre.
  const slug = registrationLinkSlug(values.name);
  if (values.previousName !== undefined) {
    const sameName = comparableName(values.name) === comparableName(values.previousName);
    if (sameName || slug === registrationLinkSlug(values.previousName)) {
      const message =
        "Ponle al nuevo torneo un nombre distinto al anterior (por ejemplo, con el año nuevo): con ese nombre se arma su link de inscripción.";
      return sameName ? message : `${message} Los puntos, comas, guiones y otros signos no cuentan.`;
    }
  }
  if (values.previousSlug && slug === values.previousSlug) {
    // El torneo anterior se renombró: su link salió de su nombre de antes.
    return `Con ese nombre el link de inscripción quedaría igual al del torneo anterior (${REGISTRATION_LINK_PREFIX}${values.previousSlug}). Ponle otro nombre (por ejemplo, con el año nuevo).`;
  }
  if (!values.startDate) return "Elige la fecha de inicio del nuevo torneo.";
  if (!values.endDate) return "Elige la fecha de fin del nuevo torneo.";
  if (!values.registrationDeadline) return "Elige el cierre de inscripciones.";
  if (values.endDate < values.startDate) return "La fecha de fin no puede ser antes de la fecha de inicio.";
  if (values.registrationDeadline > values.startDate) {
    return "El cierre de inscripciones no puede ser después de la fecha de inicio.";
  }
  return null;
}

/**
 * «Duplicar torneo» (Eduardo 2026-10-03): «solo que tenga que cambiar el
 * póster, fechas, pero que se queden sus categorías, preguntas en link de
 * inscripción». Pide nombre y fechas del nuevo torneo, crea la copia en
 * borrador y abre su Paso 1 (Datos) para subir el póster.
 */
export default function DuplicateTournamentDialog({ token, tournament, preview, onClose }: DuplicateTournamentDialogProps) {
  const ids = useId();
  const navigate = useNavigate();
  // Revisión ronda 4: ya no se propone «(copia)». Con este nombre se arma el
  // link de inscripción y renombrar después no lo cambia, así que aquí se
  // escribe el definitivo (el anterior viene seleccionado para reemplazarlo).
  const [name, setName] = useState(tournament.name.slice(0, NAME_MAX));
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [registrationDeadline, setRegistrationDeadline] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const errorRef = useRef<HTMLParagraphElement | null>(null);
  const busyRef = useRef(busy);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Al abrir: foco en el nombre y la página de atrás no se desplaza. Al
  // cerrar, el foco regresa al botón «Duplicar».
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add(DUPLICATE_SHEET_OPEN_CLASS);
    nameRef.current?.focus();
    nameRef.current?.select();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove(DUPLICATE_SHEET_OPEN_CLASS);
      if (previouslyFocused && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === "Tab") {
        const panel = panelRef.current;
        if (!panel) return;
        const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusable.length === 0) {
          // «Creando copia…»: todo está deshabilitado; el foco se queda en la
          // hoja en vez de irse a la página de atrás.
          event.preventDefault();
          panel.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        // Un clic en texto de la hoja enfoca el panel mismo (tabIndex -1):
        // cuenta como «fuera» del ciclo, si no Shift+Tab salía a la página
        // de atrás.
        if (!panel.contains(active) || active === panel) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (event.key !== "Escape" || busyRef.current) return;
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  // Al deshabilitarse el botón enfocado, el foco pasa a la hoja (no al body).
  useEffect(() => {
    if (busy) panelRef.current?.focus();
  }, [busy]);

  function requestClose() {
    if (busy) return;
    onClose();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const problem = duplicateFormError({
      name,
      startDate,
      endDate,
      registrationDeadline,
      previousName: tournament.name,
      previousSlug: tournament.slug,
    });
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const response = await api.tournaments.duplicate(token, tournament.id, {
        name: name.trim(),
        start_date: startDate,
        end_date: endDate,
        registration_deadline: registrationDeadline,
      });
      // La copia es un borrador: se abre su Paso 1 para subir el póster.
      navigate(`/admin/tournaments/${response.data.tournament.id}/edit`);
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : "No se pudo crear la copia. Intenta otra vez.");
      setBusy(false);
    }
  }

  const payment = paymentLine(preview);
  // La forma de cobro va antes del reglamento (Se copia) o del póster (No se copia).
  const copiedItems = payment.copied ? [...COPIED.slice(0, 3), payment.text, ...COPIED.slice(3)] : COPIED;
  const notCopiedItems = payment.copied ? NOT_COPIED : [payment.text, ...NOT_COPIED];
  const owner = ownerNotice(preview, tournament.name);
  // El link que se va a crear (revisión ronda 5). Si es el del torneo
  // anterior no se muestra: «Crear copia» lo explica.
  const newSlug = registrationLinkSlug(name);
  const showLink =
    name.trim() !== "" &&
    newSlug !== registrationLinkSlug(tournament.name) &&
    newSlug !== (tournament.slug ?? null);

  return createPortal(
    <div
      className="rr-change-sheet duplicate-tournament-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${ids}-eyebrow ${ids}-title`}
      aria-describedby={`${ids}-lead`}
    >
      {/* El segundo clic de un doble clic en «Duplicar» cae en el fondo
          (la hoja ya se abrió): no la cierra. */}
      <div
        className="rr-change-sheet__backdrop"
        onClick={(event) => {
          if (event.detail > 1) return;
          requestClose();
        }}
      />
      <div className="rr-change-sheet__panel" ref={panelRef} tabIndex={-1}>
        <header className="rr-change-sheet__header">
          <div>
            <span className="player-pro-eyebrow" id={`${ids}-eyebrow`}>
              Duplicar torneo
            </span>
            <strong id={`${ids}-title`}>{tournament.name}</strong>
          </div>
          <button
            type="button"
            className="rr-change-sheet__close"
            onClick={requestClose}
            disabled={busy}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <form className="duplicate-tournament-sheet__form" onSubmit={(event) => void handleSubmit(event)} noValidate>
          <div className="rr-change-sheet__body" data-no-toast>
            <p className="muted duplicate-tournament-sheet__lead" id={`${ids}-lead`}>
              Se crea un torneo nuevo en borrador con la misma configuración. Solo elige el nombre y las fechas; después
              sube el póster nuevo.
            </p>
            {owner ? <p className="duplicate-tournament-sheet__owner">{owner}</p> : null}
            <label className="rr-change-sheet__field">
              <span>Nombre del nuevo torneo</span>
              <input
                ref={nameRef}
                type="text"
                value={name}
                maxLength={NAME_MAX}
                disabled={busy}
                aria-describedby={showLink ? `${ids}-name-hint ${ids}-link` : `${ids}-name-hint`}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <p className="muted duplicate-tournament-sheet__hint" id={`${ids}-name-hint`}>
              Con este nombre se arma el link de inscripción y el link ya no cambia aunque después cambies el nombre.
              Escribe el nombre con el que lo vas a anunciar (por ejemplo, con el año nuevo).
            </p>
            {showLink ? (
              <p className="muted duplicate-tournament-sheet__hint duplicate-tournament-sheet__link" id={`${ids}-link`}>
                Link de inscripción: {REGISTRATION_LINK_PREFIX}
                {newSlug}. Si otro torneo ya usa ese link, se le agregan unas letras y números al final; el link final lo
                ves en el paso 1.
              </p>
            ) : null}
            <div className="duplicate-tournament-sheet__dates">
              <label className="rr-change-sheet__field">
                <span>Fecha de inicio</span>
                <input type="date" value={startDate} disabled={busy} onChange={(event) => setStartDate(event.target.value)} />
              </label>
              {/* Mismos límites que el Paso 1: el calendario del teléfono no
                  deja elegir un fin antes del inicio ni un cierre después. */}
              <label className="rr-change-sheet__field">
                <span>Fecha de fin</span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  disabled={busy}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </label>
              <label className="rr-change-sheet__field">
                <span>Cierre de inscripciones</span>
                <input
                  type="date"
                  value={registrationDeadline}
                  max={startDate || undefined}
                  disabled={busy}
                  onChange={(event) => setRegistrationDeadline(event.target.value)}
                />
              </label>
            </div>
            {error ? (
              <p role="alert" className="form-error" ref={errorRef} tabIndex={-1}>
                {error}
              </p>
            ) : null}
            <div className="duplicate-tournament-sheet__lists">
              <div>
                <strong>Se copia</strong>
                <ul>
                  {copiedItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>No se copia</strong>
                <ul>
                  {notCopiedItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <footer className="rr-change-sheet__footer">
            <button type="button" className="btn btn--ghost" onClick={requestClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="btn" disabled={busy}>
              {busy ? "Creando copia…" : "Crear copia"}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
